import React from 'react';
import { Alert, Box, Chip, Stack, Table, TableBody, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { Plan, TIER_COLORS } from '../../services/learningApi';
import { credits } from './common';

const Row: React.FC<{ label: string; value: React.ReactNode; help?: string }> = ({ label, value, help }) => (
  <TableRow>
    <TableCell sx={{ border: 0, py: 0.5, pl: 0, color: 'text.secondary' }}>
      {help ? (
        <Tooltip title={help} placement="left">
          <span style={{ borderBottom: '1px dotted currentColor', cursor: 'help' }}>{label}</span>
        </Tooltip>
      ) : (
        label
      )}
    </TableCell>
    <TableCell sx={{ border: 0, py: 0.5, pr: 0 }} align="right">
      {value}
    </TableCell>
  </TableRow>
);

/** What the next analysis would do: which runs, how many, and what it would cost. */
export const PlanSummary: React.FC<{ plan: Plan; compact?: boolean }> = ({ plan, compact }) => {
  const stages = (['profile', 'extract', 'review'] as const).filter((s) => plan.estimate[s]);
  const windowDays = Math.max(1, Math.round((Date.parse(plan.window_end) - Date.parse(plan.window_start)) / 86400000));
  return (
    <Box>
      {plan.blockers.map((b) => (
        <Alert key={b} severity="error" sx={{ mb: 1 }}>
          {b}
        </Alert>
      ))}
      {plan.cap_error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {plan.cap_error}
        </Alert>
      )}
      {plan.warnings.map((w) => (
        <Alert key={w} severity="warning" sx={{ mb: 1 }}>
          {w}
        </Alert>
      ))}

      <Typography variant="body1" sx={{ mb: 1 }}>
        {plan.planned > 0 ? (
          <>
            Would analyze <b>{plan.planned}</b> of the <b>{plan.eligible}</b> runs not analyzed yet ({plan.sample_percent_of_eligible}%) for about{' '}
            <b>{credits(plan.estimate.total_credits)}</b>.
          </>
        ) : (
          <>Nothing new to analyze right now.</>
        )}
      </Typography>

      <Table size="small">
        <TableBody>
          <Row
            label={`Runs in the last ${windowDays} days`}
            value={plan.total_in_window}
            help="Successful and failed runs within the look-back period (Settings → Which runs to sample → Look back)."
          />
          <Row label="Already analyzed" value={plan.already_scanned} help="Runs a previous analysis read. They are skipped unless new feedback arrived for them." />
          {plan.rescan_eligible > 0 && <Row label="Re-scan (new feedback)" value={plan.rescan_eligible} />}
          {plan.duplicates_skipped > 0 && <Row label="Duplicate inputs skipped" value={plan.duplicates_skipped} />}
          {plan.filtered_out > 0 && <Row label="Filtered out by settings" value={plan.filtered_out} />}
          <Row
            label="Not analyzed yet"
            value={plan.eligible}
            help="Runs in the look-back period that no analysis has read yet, after skipping duplicates and anything your settings filter out. This can include runs from before your last applied change."
          />
          <Row label="Planned" value={<b>{plan.planned}</b>} />
          {plan.cap_limited_runs !== null && plan.cap_limited_runs !== undefined && (
            <Row label="Limited by credit cap to" value={`${plan.cap_limited_runs} runs`} />
          )}
          {!compact &&
            stages.map((s) => {
              const st = plan.estimate[s]!;
              return (
                <Row
                  key={s}
                  label={`${s[0].toUpperCase()}${s.slice(1)} (${st.model})`}
                  value={`${st.calls} call${st.calls === 1 ? '' : 's'} · ${credits(st.credits)}`}
                />
              );
            })}
          <Row
            label="Credit cap"
            value={`${credits(plan.credit_cap)}${plan.cap_is_auto ? ' (auto)' : ''}`}
            help={`The cycle stops (keeping what it learned so far) if it reaches this. Minimum useful: ${credits(plan.min_cycle_credits)}; recommended for this plan: ${credits(plan.recommended_cap)}.`}
          />
        </TableBody>
      </Table>

      {Object.values(plan.tier_counts_planned || {}).some((n) => n > 0) && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Planned runs by signal (strongest first)
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', rowGap: 1 }}>
            {Object.entries(plan.tier_counts_planned).map(([tier, n]) => (
              <Chip key={tier} size="small" label={`${tier}: ${n}`} color={TIER_COLORS[tier] || 'default'} variant="outlined" />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};
